import { Cause, Context, Effect, Layer, Ref, Scope } from "effect";

// Keep release failures out of Layer's acquisition-failure combination: that
// combination structurally hashes private payloads in the pinned Effect version.
// The root later returns every original reason after all resources have settled.
export const makeLayerOwner = Effect.fn("Frontend.makeLayerOwner")(function* () {
  const failures = yield* Ref.make<readonly Cause.Reason<never>[]>([]);
  const base = yield* Layer.makeMemoMap;
  const ownFinalizers = (resources: Scope.Scope) => Effect.sync(() => {
    if (resources.state._tag === "Closed") return;
    const previous = resources.state._tag === "Open" ? resources.state.finalizers : [];
    const finalizers: Scope.State.Open["finalizers"] = new Map();
    const retain = finalizers.set.bind(finalizers);
    finalizers.set = (key, release) => retain(key, exit => Effect.exit(Effect.suspend(() => release(exit))).pipe(
      Effect.flatMap(result => result._tag === "Failure"
        ? Ref.update(failures, reasons => [...reasons, ...result.cause.reasons]) : Effect.void)
    ));
    for (const [key, release] of previous) finalizers.set(key, release);
    resources.state = { _tag: "Open", finalizers };
  });
  const getOrElseMemoize = <RIn, E, ROut>(
    layer: Layer.Layer<ROut, E, RIn>,
    scope: Scope.Scope,
    build: (memoMap: Layer.MemoMap, scope: Scope.Scope) => Effect.Effect<Context.Context<ROut>, E, RIn>
  ): Effect.Effect<Context.Context<ROut>, E, RIn> => base.getOrElseMemoize(layer, scope, (_, resources) =>
    ownFinalizers(resources).pipe(Effect.andThen(Effect.suspend(() => build(memoMap, resources))))
  );
  const memoMap: Layer.MemoMap = new Proxy(base, { get: (target, property, receiver) =>
    property === "getOrElseMemoize" ? getOrElseMemoize : Reflect.get(target, property, receiver)
  });
  const cleanupResult = Ref.get(failures).pipe(Effect.flatMap(reasons => reasons.length ? Effect.failCause(Cause.fromReasons(reasons)) : Effect.void));
  return { memoMap, cleanupResult };
});
